import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";
import { IconButton, TextField } from "@material-ui/core";
import { BaseModal } from "Components/Modal/BaseModal";
import { CancelButton } from "Globals/Styles/Control/Buttons";
import { Button } from "@material-ui/core";
import { Cell, Grid, useRouter } from "@shoothill/core";
import { DarwinDateSelect } from "Components/DateSelect/DarwinDateSelect";
import { AddEditResponseViewModel } from "./AddEditResponseViewModel";
import { ReportModalStyle } from "Views/Project/Modals/Modal.Styles";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { RDDV2CreateFormViewModel } from "../../Forms/Create/RDDV2CreateFormViewModel";
import { Uploader } from "Components/Uploader/Uploader";
import SystemUpdateTwoToneIcon from "@material-ui/icons/SystemUpdateTwoTone";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { RDDV2ResponseDocumentDTO } from "./AddEditResponseModel";
import moment from "moment";

interface ICreatedModalProps {
    open?: boolean;
    onClose(): void;
    rddV2Id: string | null;
    id: string | null;
}

export const AddEditResponseView: React.FC<ICreatedModalProps> = (props) => {
    const { match } = useRouter();
    const { projectid } = match.params as any;
    const { open, onClose, rddV2Id, id } = props;
    const [viewModel] = useState(() => new AddEditResponseViewModel(projectid, rddV2Id, id));
    const [rddV2CreateFormViewModel] = useState(() => new RDDV2CreateFormViewModel(projectid));
    const COLUMNS8 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

    const onSave = () => {
        let promise = viewModel.upsert();
        promise.then((response) => {
            if (response.wasSuccessful) {
                onClose();
            }
        });
    };

    return useObserver(() => (
        <BaseModal
            open={open}
            onClose={onClose}
            title={viewModel.hasId ? "Edit response" : "Add response"}
            PaperComponent={ReportModalStyle}
            actions={
                <>
                    <Button onClick={onSave} color="secondary" autoFocus variant="contained">
                        {viewModel.hasId ? "Update" : "Add"}
                    </Button>
                    <CancelButton onClick={onClose}>Cancel</CancelButton>
                </>
            }
        >
            <Grid columnGap={30} rowGap={20} tc={COLUMNS8} dc={COLUMNS8} mb="35px">
                <Cell ts={8} ds={8}>
                    <TextField
                        id="note-textarea"
                        multiline
                        minRows={5}
                        value={viewModel.getValue("comment")}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => viewModel.setValue<string>("comment", event.target.value)}
                        fullWidth
                        className="textarea-forminput"
                        label="Response:"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        placeholder={`Please add`}
                        disabled={false}
                        error={viewModel.getError("comment") !== ""}
                        helperText={viewModel.getError("comment")}
                        onBlur={() => viewModel.isFieldValid("comment")}
                    />
                </Cell>
                <Cell ts={2} ds={2}>
                    <DarwinDateSelect
                        displayName="Date received:"
                        execute={(value: string | null) => viewModel.setReceivedDate(value, true)}
                        onBlur={() => viewModel.isFieldValid("receivedDate")}
                        placeholder="Please select"
                        validationMessage={viewModel.getError("receivedDate")}
                        value={
                            viewModel.getValue("receivedDate") === null ? viewModel.setReceivedDate(moment().format("YYYY-MM-DDTHH:mm:ss[Z]")) : viewModel.getValue("receivedDate")
                        }
                        canExecute={!viewModel.isFormDisabled && !viewModel.IsLoading}
                    />
                </Cell>
                <Cell ts={2} ds={2}>
                    <DarwinDateSelect
                        displayName="Response required by:"
                        execute={(value: string | null) => viewModel.setResponseRequiredBy(value)}
                        onBlur={() => viewModel.isFieldValid("responseRequiredBy")}
                        placeholder="Please select"
                        validationMessage={viewModel.getError("responseRequiredBy")}
                        value={
                            viewModel.getValue("responseRequiredBy") === null
                                ? viewModel.setResponseRequiredBy(moment().add(1, "weeks").format("YYYY-MM-DDTHH:mm:ss[Z]"))
                                : viewModel.getValue("responseRequiredBy")
                        }
                        canExecute={!viewModel.isFormDisabled && !viewModel.IsLoading}
                    />
                </Cell>
                <Cell ts={2} ds={2}>
                    <DarwinSelect
                        displayName="From:"
                        execute={(data: any) => viewModel.handleProjectDirectoryPersonId(data)}
                        fullWidth={true}
                        getOptionLabel={(option: any) => option.displayName}
                        options={rddV2CreateFormViewModel.projectDirectoryPersons}
                        placeholder="Please select"
                        value={viewModel.projectDirectoryPerson}
                        canExecute={!viewModel.IsLoading}
                        onBlur={() => viewModel.isFieldValid("projectDirectoryPersonId")}
                        error={viewModel.getError("projectDirectoryPersonId") !== ""}
                        validationMessage={viewModel.getError("projectDirectoryPersonId")}
                    />
                </Cell>
                <Cell ts={2} ds={2}>
                    <DarwinSelect
                        displayName="Status:"
                        execute={(data: any) => viewModel.handleStatusId(data)}
                        fullWidth={true}
                        getOptionLabel={(option: any) => option.displayName}
                        options={rddV2CreateFormViewModel.statuses}
                        placeholder="Please select"
                        value={viewModel.status}
                        canExecute={!viewModel.IsLoading}
                        onBlur={() => viewModel.isFieldValid("statusId")}
                        error={viewModel.getError("statusId") !== ""}
                        validationMessage={viewModel.getError("statusId")}
                    />
                </Cell>
                <Cell ts={8} ds={8}>
                    <Uploader handleSelectFile={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.fileChange(e)} isFormDisabled={viewModel.IsLoading} label="Documents" />

                    <div className="uploadedFile">
                        {viewModel.rddV2ResponseDocuments.map((item: RDDV2ResponseDocumentDTO, index: number) => {
                            return (
                                <div className="uploadedFile-box">
                                    <div className="uploadedFile-heading">
                                        <IconButton onClick={() => viewModel.DownloadFile(item.url, item.fileName)}>
                                            <SystemUpdateTwoToneIcon />
                                        </IconButton>
                                        <h3>{item.fileName}</h3>
                                    </div>
                                    <IconButton onClick={() => viewModel.handleDeleteDocument(item.id)} disabled={viewModel.IsLoading}>
                                        <DeleteOutlinedIcon />
                                    </IconButton>
                                </div>
                            );
                        })}
                    </div>
                </Cell>
            </Grid>
        </BaseModal>
    ));
};
